import React, { useState } from 'react';
import { cn } from '../../lib/cn';
import { Icon } from '../Icon/Icon';
import type { IconKind } from '../Icon/Icon';
import { RECALL_AGENT } from '../../settings-recall-agent/mockData';
import { CHANNEL_ICON, CHANNEL_LABEL } from '../../recall-builder/types';
import type { AudiencePatient, BuilderStep, RecallDraft } from '../../recall-builder/types';
import {
  OPT_OUT_LINE,
  canStart,
  contactableNow,
  gapLabel,
  previewPatients,
  resolveForPatient,
  stepIssues,
  textCounts,
  recallLanguageLabel,
} from '../../recall-builder/derive';
import { activeLanguages } from '../../platform-languages';
import type { LanguageCode } from '../../platform-languages';
import { NO_BRIEF_YET, SMART_PREVIEW, TEST_SENDS } from '../../recall-builder/mockData';
import type { TestSendRow } from '../../recall-builder/mockData';
import { BuilderButton, BuilderFooter, BuilderHeader, BuilderTopBar, BuilderPage, FixList } from './BuilderShell';

export interface TestStartViewProps {
  draft: RecallDraft;
  onChange: (next: RecallDraft) => void;
  onBack: () => void;
  onEditStep: (stepId: string) => void;
  onSaveDraft: () => void;
  onStart: () => void;
  /** Editing a running recall swaps the start card for the save card. */
  running?: boolean;
}

/**
 * Page 3 — Test and start (spec 21 §4).
 *
 * Built from the reference with its failures removed: **every step renders** —
 * there is no *checking condition* placeholder standing where a preview should
 * be — the steps list is plain and readable, all steps show rather than one, and
 * there are no channel tabs or segments, because each step has one channel and V1
 * has one audience.
 */
export function TestStartView({
  draft,
  onChange,
  onBack,
  onEditStep,
  onSaveDraft,
  onStart,
  running = false,
}: TestStartViewProps) {
  const patients = previewPatients(draft);
  const [patientId, setPatientId] = useState(patients[0]?.id);
  const [stepId, setStepId] = useState(draft.steps[0]?.id);
  const [phone, setPhone] = useState('(210) 555-0100');
  const [email, setEmail] = useState('amir@elva.ai');
  // §4.3 — the list is seeded, so its shape (including a failure and its
  // reason) is visible without sending anything first.
  const [sent, setSent] = useState<TestSendRow[]>(TEST_SENDS);
  // Which language a test send goes in (amendment 25 §7). Without it the test
  // could only ever exercise English, which is the one language we are least
  // likely to get wrong.
  const [sendLanguage, setSendLanguage] = useState<LanguageCode>('en');
  const [showBackup, setShowBackup] = useState(false);

  const issues = stepIssues(draft);
  const start = canStart(draft);
  const patient = patients.find((p) => p.id === patientId) ?? patients[0];
  const stepIndex = draft.steps.findIndex((s) => s.id === stepId);
  const step = draft.steps[stepIndex] ?? draft.steps[0];

  const send = (label: string) => {
    const now = new Date();
    setSent((prev: TestSendRow[]) => [
      {
        id: `t${String(Date.now())}`,
        label,
        status: 'delivered',
        at: `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`,
      },
      ...prev,
    ]);
  };

  return (
    <BuilderPage>
      <BuilderHeader
        active="test"
        titleLead="Check it, then"
        titleAccent="start"
        sub="See each step the way a patient will, send it to yourself, and start when it’s right."
        reachable={['audience', 'messages']}
        onGoTo={(s) => { if (s !== 'test') onBack(); }}
      />

      <BuilderTopBar
        draft={draft}
        onName={(name) => { onChange({ ...draft, name }); }}
        onChangeGoal={onBack}
        onChangeAudience={onBack}
      />

      <div className="mt-5 flex items-start gap-5 max-[1100px]:flex-col">
        {/* The preview. */}
        <div className="min-w-0 flex-1 rounded-[16px] border border-[var(--border-default)] bg-[var(--surface-card)]">
          <header className="flex flex-wrap items-center justify-between gap-3 border-b border-[var(--border-default)] px-5 py-3.5">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[11.5px] font-semibold text-[var(--text-muted)]">Preview as</span>
              {patients.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => { setPatientId(p.id); }}
                  className={cn(
                    'rounded-[10px] px-2.5 py-1 text-left',
                    p.id === patient?.id
                      ? 'bg-[var(--brand-indigo)] text-white'
                      : 'bg-[var(--surface-sunken)] text-[var(--text-secondary)]',
                  )}
                >
                  <span className="block text-[12px] font-semibold">{p.name}</span>
                  {/* The language this one will actually get (§7). */}
                  <span
                    className={cn(
                      'block text-[10px]',
                      p.id === patient?.id ? 'text-white/80' : 'text-[var(--text-muted)]',
                    )}
                  >
                    {recallLanguageLabel(p)}
                  </span>
                </button>
              ))}
            </div>
            <span className="text-[11px] text-[var(--text-muted)]">
              Real patients from this recall — shown here only, never sent.
            </span>
          </header>

          {step === undefined || patient === undefined ? (
            <p className="m-0 px-5 py-10 text-center text-[13px] text-[var(--text-muted)]">
              Add a step on the previous page to preview it here.
            </p>
          ) : (
            <StepPreview
              step={step}
              index={stepIndex < 0 ? 0 : stepIndex}
              patient={patient}
              showBackup={showBackup}
              onToggleBackup={() => { setShowBackup(!showBackup); }}
              onEdit={() => { onEditStep(step.id); }}
              humanCallOpenDays={RECALL_AGENT.deadlineOpenDays}
            />
          )}
        </div>

        {/* The steps, and sending one to yourself. */}
        <div className="w-[392px] shrink-0 max-[1100px]:w-full">
          <div className="rounded-[16px] border border-[var(--border-default)] bg-[var(--surface-card)] p-4">
            <div className="text-[10.5px] font-bold uppercase tracking-[0.09em] text-[var(--text-muted)]">
              The steps
            </div>
            <div className="mt-2 flex flex-col">
              {draft.steps.map((s, i) => {
                const mine = issues.filter((x) => x.stepId === s.id);
                const next = draft.steps[i + 1];
                return (
                  <React.Fragment key={s.id}>
                    <button
                      type="button"
                      onClick={() => { setStepId(s.id); }}
                      className={cn(
                        'flex items-center gap-2.5 rounded-[10px] px-2.5 py-2 text-left',
                        s.id === step?.id ? 'bg-[var(--brand-indigo-tint)]' : 'hover:bg-[var(--surface-sunken)]',
                      )}
                    >
                      <Icon kind={CHANNEL_ICON[s.channel] as IconKind} size={13} stroke={2} className="shrink-0 text-[var(--text-muted)]" />
                      <span className="min-w-0 flex-1">
                        <span className="text-[12.5px] font-semibold text-[var(--text-primary)]">
                          {CHANNEL_LABEL[s.channel]}
                        </span>
                        <span className="ml-1.5 font-[var(--font-mono)] text-[11px] text-[var(--text-muted)]">
                          day {s.day}
                        </span>
                      </span>
                      {s.channel === 'human_call' ? (
                        <span className="shrink-0 text-[11px] text-[var(--text-muted)]">A task in Triage Inbox</span>
                      ) : mine.length === 0 ? (
                        <span className="shrink-0 text-[11.5px] font-semibold text-[var(--money-green)]">✓ Ready</span>
                      ) : (
                        <span className="shrink-0 text-[11px] font-semibold text-[var(--alert-red)]">{mine[0]?.short}</span>
                      )}
                      <span
                        role="link"
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditStep(s.id);
                        }}
                        className="shrink-0 text-[11.5px] font-semibold text-[var(--brand-indigo)] hover:underline"
                      >
                        Edit
                      </span>
                    </button>
                    {next ? <span className="py-0.5 pl-9 text-[10.5px] text-[var(--text-muted)]">{gapLabel(s, next)}</span> : null}
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          <div className="mt-4 rounded-[16px] border border-[var(--border-default)] bg-[var(--surface-card)] p-4">
            <div className="text-[12.5px] font-bold text-[var(--text-primary)]">Send it to yourself</div>
            <p className="m-0 mt-1 text-[11px] leading-[1.55] text-[var(--text-muted)]">
              Uses a made-up sample patient and is marked [Test]. Nothing is counted in the recall’s results.
            </p>
            <div className="mt-2.5 flex flex-col gap-2">
              <label className="text-[11px] font-semibold text-[var(--text-muted)]">
                Phone
                <input
                  value={phone}
                  onChange={(e) => { setPhone(e.target.value); }}
                  className="mt-1 w-full rounded-[9px] border border-[#dfe4ee] px-2.5 py-1.5 text-[12.5px] font-normal text-[var(--text-primary)] outline-none transition-[border-color,box-shadow] hover:border-[#ccd4e4] focus:border-[var(--brand-indigo)] focus:shadow-[0_0_0_3px_rgba(76,91,212,0.14)]"
                />
              </label>
              <label className="text-[11px] font-semibold text-[var(--text-muted)]">
                Email
                <input
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); }}
                  className="mt-1 w-full rounded-[9px] border border-[#dfe4ee] px-2.5 py-1.5 text-[12.5px] font-normal text-[var(--text-primary)] outline-none transition-[border-color,box-shadow] hover:border-[#ccd4e4] focus:border-[var(--brand-indigo)] focus:shadow-[0_0_0_3px_rgba(76,91,212,0.14)]"
                />
              </label>
            </div>
            <label className="mt-2 block text-[11px] font-semibold text-[var(--text-muted)]">
              Language
              <select
                value={sendLanguage}
                aria-label="Language to send in"
                onChange={(e) => {
                  setSendLanguage(e.target.value as LanguageCode);
                }}
                className="mt-1 w-full rounded-[9px] border border-[#dfe4ee] bg-[var(--surface-card)] px-2 py-1.5 text-[12.5px] font-normal text-[var(--text-primary)] outline-none transition-[border-color,box-shadow] hover:border-[#ccd4e4] focus:border-[var(--brand-indigo)] focus:shadow-[0_0_0_3px_rgba(76,91,212,0.14)]"
              >
                {activeLanguages().map((l) => (
                  <option key={l.code} value={l.code}>
                    {l.name}
                  </option>
                ))}
              </select>
            </label>
            <div className="mt-2.5 flex flex-wrap gap-2">
              {/* Nothing is sent for a human call — it makes a task. Offering
                  *Send step 4* for one was a button that could not work (§4.1). */}
              {step === undefined || step.channel === 'human_call' ? null : (
                <BuilderButton
                  onClick={() => {
                    send(`Step ${String(stepIndex + 1)} · ${CHANNEL_LABEL[step.channel].toLowerCase()} to ${step.channel === 'email' ? email : phone}`);
                  }}
                >
                  Send step {stepIndex + 1}
                </BuilderButton>
              )}
              <BuilderButton
                kind="soft"
                onClick={() => {
                  draft.steps.forEach((s, i) => {
                    if (s.channel === 'human_call') return;
                    send(`Step ${String(i + 1)} · ${CHANNEL_LABEL[s.channel].toLowerCase()} to ${s.channel === 'email' ? email : phone}`);
                  });
                }}
              >
                Send every step
              </BuilderButton>
            </div>
            <p className="m-0 mt-2 text-[11px] leading-[1.55] text-[var(--text-muted)]">
              Every step goes now, one minute apart. The AI call rings your phone. The human call isn’t sent — you’ll
              see the task it creates.
            </p>

            {sent.length > 0 ? (
              <div className="mt-3 border-t border-[var(--border-default)] pt-2.5">
                <div className="text-[10.5px] font-bold uppercase tracking-[0.09em] text-[var(--text-muted)]">
                  Sent in the last hour
                </div>
                <div className="mt-1.5 flex flex-col gap-1">
                  {sent.slice(0, 6).map((s) => (
                    <div key={s.id} className="flex items-baseline justify-between gap-2 text-[11.5px]">
                      <span className="min-w-0 truncate text-[var(--text-secondary)]">
                        {s.label} ·{' '}
                        <b
                          className="font-semibold"
                          style={{ color: s.status === 'delivered' ? 'var(--money-green)' : 'var(--alert-red)' }}
                        >
                          {s.status}
                        </b>
                        {s.reason !== undefined ? ` — ${s.reason}` : ''}
                      </span>
                      <span className="shrink-0 font-[var(--font-mono)] text-[10.5px] text-[var(--text-muted)]">
                        {s.at}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {running ? <SaveCard draft={draft} /> : <StartCard draft={draft} ok={start.ok} issues={issues} onEditStep={onEditStep} />}

      <BuilderFooter>
        <BuilderButton onClick={onBack}>Back</BuilderButton>
        <BuilderButton onClick={onSaveDraft}>{running ? 'Discard' : 'Save as draft'}</BuilderButton>
        <BuilderButton
          kind="primary"
          disabled={!start.ok}
          title={start.ok ? undefined : 'Fix everything listed above first'}
          onClick={onStart}
        >
          {running ? 'Save changes' : 'Start the recall'}
        </BuilderButton>
      </BuilderFooter>
    </BuilderPage>
  );
}

/** The four facts, **computed** — not one of them is written into the page. */
function StartCard({
  draft,
  ok,
  issues,
  onEditStep,
}: {
  draft: RecallDraft;
  ok: boolean;
  issues: ReturnType<typeof stepIssues>;
  onEditStep: (id: string) => void;
}) {
  const lastDay = draft.steps[draft.steps.length - 1]?.day ?? 0;
  const patients = contactableNow(draft);
  // If today's sending hours for day 0's channel have passed, the first messages
  // go tomorrow — and the card says which rather than implying today.
  const hour = new Date().getHours();
  const today = hour < 17;

  return (
    <div
      className="mt-5 overflow-hidden rounded-[16px] border bg-[var(--surface-card)]"
      style={{ borderColor: ok ? 'var(--money-green)' : 'var(--alert-red)', borderTopWidth: 3 }}
    >
      <div className="px-5 py-4">
        {ok ? (
          <div className="flex items-center gap-1.5 text-[13px] font-bold text-[var(--money-green)]">
            <Icon kind="check" size={14} stroke={3} />
            Every step is ready
          </div>
        ) : (
          <FixList issues={issues} onGoToStep={onEditStep} />
        )}

        <div className="mt-3.5 grid grid-cols-4 gap-4 max-[1100px]:grid-cols-2">
          <Fact big={patients.toLocaleString('en-US')} sub="patients start as soon as you start it" />
          <Fact
            big={today ? 'today' : 'tomorrow'}
            sub={today ? 'first texts go out, between 9:00 and 17:00' : 'first texts go out at 9:00'}
          />
          <Fact big={`day ${String(lastDay)}`} sub="the last step — then the recall ends for them" />
          <Fact big="nightly" sub="new patients who match are added" />
        </div>
      </div>
    </div>
  );
}

function Fact({ big, sub }: { big: string; sub: string }) {
  return (
    <div>
      <div className="font-[var(--font-mono)] text-[20px] font-bold tracking-[-0.02em] text-[var(--text-primary)]">
        {big}
      </div>
      <div className="mt-0.5 text-[11.5px] leading-[1.5] text-[var(--text-muted)]">{sub}</div>
    </div>
  );
}

/**
 * Editing a running recall — what the change will do, **in the patients' own
 * numbers** (P46). Content reaches everyone; steps and audience reach new
 * patients only, so a patient three steps in is not re-sorted underneath
 * themselves.
 */
function SaveCard({ draft }: { draft: RecallDraft }) {
  const inFlight = draft.count?.contactable ?? 0;
  return (
    <div className="mt-5 rounded-[16px] border border-[var(--border-default)] bg-[var(--surface-card)] px-5 py-4">
      <div className="text-[13px] font-bold text-[var(--text-primary)]">What this change will do</div>
      <ul className="m-0 mt-2 flex list-none flex-col gap-1.5 p-0 text-[12.5px] leading-[1.6] text-[var(--text-secondary)]">
        <li>
          <b className="font-semibold text-[var(--text-primary)]">Wording</b> reaches all{' '}
          {inFlight.toLocaleString('en-US')} patients — the next message out is the new one.
        </li>
        <li>
          <b className="font-semibold text-[var(--text-primary)]">Steps and who gets it</b> apply to patients who start
          the recall from now on. Nobody part-way through is re-sorted or sent a step twice.
        </li>
      </ul>
    </div>
  );
}

/** One real preview per channel — and no loading placeholder standing in for one. */
function StepPreview({
  step,
  index,
  patient,
  showBackup,
  onToggleBackup,
  onEdit,
  humanCallOpenDays,
}: {
  step: BuilderStep;
  index: number;
  patient: AudiencePatient;
  showBackup: boolean;
  onToggleBackup: () => void;
  onEdit: () => void;
  humanCallOpenDays: number;
}) {
  const smart = step.mode === 'smart' && !showBackup;
  const tokens = step.mode === 'smart' ? (step.backup ?? []) : (step.body ?? []);
  const resolved = resolveForPatient(tokens, patient, step.fallbacks);
  const subject = resolveForPatient(step.subject ?? [], patient, step.fallbacks);
  const generated = SMART_PREVIEW[patient.id];
  const counts = textCounts(tokens);
  const briefEmpty = (step.brief ?? '').trim() === '';
  // The booking link becomes a button in an email, and nothing at all in a call
  // — never the literal words `[Pick a time]` (§3.2).
  const usesBookingLink =
    step.mode === 'smart'
      ? step.mustInclude?.includes('booking_link') ?? true
      : tokens.some((t) => t.kind === 'field' && t.field === 'booking_link');

  return (
    <div className="px-5 py-4">
      <div className="text-[13px] font-bold text-[var(--text-primary)]">
        Step {index + 1} · {CHANNEL_LABEL[step.channel]} · day {step.day}
      </div>
      <div className="mt-0.5 text-[11.5px] text-[var(--text-muted)]">
        {step.channel === 'human_call'
          ? 'A task for your team — not a message to the patient'
          : step.mode === 'smart'
            ? 'Smart — ELVA writes this for each patient'
            : 'Template — every patient gets these words, with their own details filled in'}
      </div>

      {step.mode === 'smart' ? (
        <div className="mt-2 flex items-center gap-3">
          <button type="button" onClick={onToggleBackup} className="text-[11.5px] font-semibold text-[var(--brand-indigo)] hover:underline">
            {showBackup ? 'Show what ELVA writes' : 'Show the backup'}
          </button>
          <button type="button" className="text-[11.5px] font-semibold text-[var(--brand-indigo)] hover:underline">
            Write again
          </button>
        </div>
      ) : null}

      <div className="mt-3 flex flex-wrap items-start gap-5">
        <div className="w-[300px] shrink-0">
          {step.channel === 'text' ? (
            <PhoneFrame practice={patient.values.practice_name ?? 'The practice'}>
              {smart ? (generated?.body ?? '') : resolved.text}
            </PhoneFrame>
          ) : step.channel === 'email' ? (
            <InboxFrame
              from={patient.values.practice_name ?? 'The practice'}
              subject={smart ? (generated?.subject ?? '') : subject.text}
              cta={usesBookingLink}
            >
              {smart ? (generated?.body ?? '') : resolved.text}
            </InboxFrame>
          ) : step.channel === 'ai_call' ? (
            // A call is spoken. It showed the text-message wording before (§3.1).
            <CallFrame empty={briefEmpty && smart}>
              {smart ? (generated?.spoken ?? '') : resolved.text}
            </CallFrame>
          ) : (
            <TaskFrame days={humanCallOpenDays} note={step.callerNote ?? ''} patient={patient.name} />
          )}
        </div>

        <div className="min-w-[220px] flex-1">
          <Detail label="When">
            Day {step.day} — the day they start the recall.{' '}
            {step.channel === 'text' ? 'Texts go out between 9:00 and 17:00.' : ''}
          </Detail>
          {step.channel === 'text' ? (
            <Detail label="Length">
              <b className="font-[var(--font-mono)] font-bold">{counts.segments}</b> text message
              {counts.segments === 1 ? '' : 's'} · {counts.chars} characters, including the opt-out line
            </Detail>
          ) : null}
          {step.channel !== 'human_call' && resolved.used.length > 0 ? (
            <Detail label="What each field became">
              <span className="mt-1 flex flex-col gap-1">
                {resolved.used.map((u, i) => (
                  <span key={`${u.field}-${String(i)}`} className="flex items-baseline gap-2 text-[11.5px]">
                    <span className="rounded-[5px] bg-[var(--brand-indigo-tint)] px-1.5 py-0.5 font-semibold text-[var(--brand-indigo)]">
                      {u.label}
                    </span>
                    <span className="text-[var(--text-secondary)]">
                      {u.value}
                      {/* The fallback is named, so a preview can never look right
                          for the wrong reason. */}
                      {u.wasFallback ? (
                        <span className="ml-1 text-[var(--warn-amber)]">— they have none on file, so this is what we say</span>
                      ) : null}
                    </span>
                  </span>
                ))}
              </span>
            </Detail>
          ) : null}
          <button type="button" onClick={onEdit} className="mt-2 text-[12.5px] font-semibold text-[var(--brand-indigo)] hover:underline">
            Edit this step →
          </button>
        </div>
      </div>
    </div>
  );
}

function Detail({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mb-2.5">
      <div className="text-[10px] font-bold uppercase tracking-[0.09em] text-[var(--text-muted)]">{label}</div>
      <div className="mt-0.5 text-[12px] leading-[1.6] text-[var(--text-secondary)]">{children}</div>
    </div>
  );
}

function PhoneFrame({ practice, children }: { practice: string; children: React.ReactNode }) {
  return (
    <div className="rounded-[18px] border border-[var(--border-default)] bg-[#FAFBFE] p-3">
      <div className="text-center text-[11px] font-semibold text-[var(--text-muted)]">{practice}</div>
      <div className="mt-2 rounded-[14px] rounded-bl-[4px] bg-[var(--brand-indigo)] px-3 py-2 text-[12px] leading-[1.6] text-white">
        {children}
      </div>
      <div className="mt-1 text-[10.5px] text-[var(--text-muted)]">{OPT_OUT_LINE}</div>
    </div>
  );
}

function InboxFrame({
  from,
  subject,
  cta,
  children,
}: {
  from: string;
  subject: string;
  cta: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="overflow-hidden rounded-[12px] border border-[var(--border-default)]">
      <div className="border-b border-[var(--border-default)] bg-[#FAFBFE] px-3 py-2">
        <div className="text-[11px] text-[var(--text-muted)]">{from}</div>
        <div className="text-[12.5px] font-bold text-[var(--text-primary)]">{subject}</div>
      </div>
      <div className="px-3 py-2.5 text-[12px] leading-[1.7] text-[var(--text-secondary)]">
        {children}
        {/* A real button — the link was showing as the words `[Pick a time]` (§3.2). */}
        {cta ? (
          <span className="mt-2.5 block">
            <span className="inline-block rounded-[8px] bg-[var(--brand-indigo)] px-3 py-1.5 text-[12px] font-semibold text-white">
              Pick a time
            </span>
          </span>
        ) : null}
      </div>
    </div>
  );
}

function CallFrame({ empty, children }: { empty: boolean; children: React.ReactNode }) {
  return (
    <div className="rounded-[12px] border border-[var(--border-default)] bg-[#FAFBFE] p-3">
      <div className="text-[10.5px] font-bold uppercase tracking-[0.08em] text-[var(--text-muted)]">
        How the call opens
      </div>
      {empty ? (
        <p className="m-0 mt-1.5 text-[12px] italic leading-[1.7] text-[var(--text-muted)]">{NO_BRIEF_YET}</p>
      ) : (
        <p className="m-0 mt-1.5 text-[12px] leading-[1.7] text-[var(--text-secondary)]">“{children}”</p>
      )}
      <button type="button" className="mt-2.5 text-[12px] font-semibold text-[var(--brand-indigo)] hover:underline">
        Call me with this →
      </button>
    </div>
  );
}

function TaskFrame({ days, note, patient }: { days: number; note: string; patient: string }) {
  return (
    <div className="rounded-[12px] border border-[var(--border-default)] bg-[#FAFBFE] p-3">
      <div className="text-[12.5px] font-bold text-[var(--text-primary)]">Call {patient}</div>
      <div className="mt-1 text-[11.5px] leading-[1.6] text-[var(--text-secondary)]">
        Due in {days} open days. Carries why they are being called and what the earlier steps already said.
      </div>
      {note.trim() === '' ? null : <div className="mt-1.5 text-[11.5px] text-[var(--text-muted)]">{note}</div>}
    </div>
  );
}
